import React, { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, HelpCircle } from 'lucide-react';
import { themeTableClasses } from './Theme';

export interface Column<T> {
    header: string;
    accessor: keyof T | ((item: T) => any);
    id: string;
    sortable?: boolean;
    className?: string;
    headerClassName?: string;
    headerInfo?: string;
    render?: (item: T) => React.ReactNode;
    align?: 'left' | 'center' | 'right';
}

interface DataTableProps<T> {
    data: T[];
    columns: Column<T>[];
    className?: string;
    loading?: boolean;
    emptyMessage?: string;
    onRowClick?: (item: T) => void;
    rowClassName?: string | ((item: T) => string);
    initialSort?: { id: string; direction: 'asc' | 'desc' };
    pageSize?: number;
    compact?: boolean;
    getRowKey?: (item: T, index: number) => string | number;
    expandedRowKey?: string | number | null;
    renderExpandedRow?: (item: T) => React.ReactNode;
}

export function DataTable<T>({
    data,
    columns,
    className = '',
    loading = false,
    emptyMessage = 'No data available',
    onRowClick,
    rowClassName,
    initialSort,
    compact = false,
    getRowKey,
    expandedRowKey = null,
    renderExpandedRow,
}: DataTableProps<T>) {
    const [sortConfig, setSortConfig] = useState<{ id: string; direction: 'asc' | 'desc' } | null>(
        initialSort || null
    );

    const handleSort = (columnId: string) => {
        setSortConfig((prev) => {
            if (prev?.id === columnId) {
                if (prev.direction === 'asc') {
                    return { id: columnId, direction: 'desc' };
                }
                return null; // Reset sort
            }
            return { id: columnId, direction: 'asc' };
        });
    };

    const sortedData = useMemo(() => {
        const safeData = data || [];
        if (!sortConfig) return safeData;

        const column = columns.find((c) => c.id === sortConfig.id);
        if (!column) return safeData;

        return [...safeData].sort((a, b) => {
            const aValue = typeof column.accessor === 'function' ? column.accessor(a) : a[column.accessor as keyof T];
            const bValue = typeof column.accessor === 'function' ? column.accessor(b) : b[column.accessor as keyof T];

            if (aValue === bValue) return 0;
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            const order = sortConfig.direction === 'asc' ? 1 : -1;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return aValue.localeCompare(bValue) * order;
            }

            return (aValue < bValue ? -1 : 1) * order;
        });
    }, [data, sortConfig, columns]);

    return (
        <div className={`overflow-x-auto scrollbar-thin rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl ${className}`}>
            <table className={`${themeTableClasses.table} divide-y divide-white/10 ${compact ? 'text-[11px]' : themeTableClasses.bodyText}`}>
                <thead className={themeTableClasses.head}>
                    <tr>
                        {columns.map((column) => (
                            <th
                                key={column.id}
                                scope="col"
                                className={`${themeTableClasses.headerCell} ${compact ? 'px-2.5 py-2 text-[10px]' : ''} ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
                                    } ${column.headerClassName || ''} ${column.sortable ? 'cursor-pointer select-none hover:text-white hover:bg-white/5 transition-colors' : ''}`}
                                onClick={() => column.sortable && handleSort(column.id)}
                            >
                                <div className={`flex items-center gap-1.5 ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : 'justify-start'
                                    }`}>
                                    {column.header}
                                    {column.headerInfo && (
                                        <span
                                            className="inline-flex shrink-0 text-white/35"
                                            title={column.headerInfo}
                                            aria-label={`${column.header}: ${column.headerInfo}`}
                                        >
                                            <HelpCircle className="h-3.5 w-3.5" />
                                        </span>
                                    )}
                                    {column.sortable && (
                                        <div className="text-white/40 group-hover:text-white transition-colors">
                                            {sortConfig?.id === column.id ? (
                                                sortConfig.direction === 'asc' ? (
                                                    <ArrowUp className="w-3.5 h-3.5 text-neon" />
                                                ) : (
                                                    <ArrowDown className="w-3.5 h-3.5 text-neon" />
                                                )
                                            ) : (
                                                <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className={`${themeTableClasses.body} bg-transparent`}>
                    {sortedData.length === 0 && !loading ? (
                        <tr>
                            <td colSpan={columns.length} className={`${themeTableClasses.cell} py-8 text-center text-muted`}>
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : sortedData.length === 0 && loading ? (
                        <tr>
                            <td colSpan={columns.length} className={`${themeTableClasses.cell} py-12 text-center text-muted`}>
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-6 h-6 border-2 border-neon/30 border-t-neon rounded-full animate-spin" />
                                    <span>Loading data...</span>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        sortedData.map((item, index) => {
                            const customClassName = typeof rowClassName === 'function' ? rowClassName(item) : rowClassName;
                            const rowKey = getRowKey ? getRowKey(item, index) : index;
                            const isExpanded = expandedRowKey !== null
                                && expandedRowKey !== undefined
                                && getRowKey
                                && rowKey === expandedRowKey;
                            return (
                                <React.Fragment key={rowKey}>
                                    <tr
                                        onClick={() => onRowClick?.(item)}
                                        className={`${themeTableClasses.row} ${onRowClick ? 'cursor-pointer' : ''
                                            } ${customClassName || ''}`}
                                    >
                                        {columns.map((column) => (
                                            <td
                                                key={column.id}
                                                className={`${themeTableClasses.cell} ${compact ? 'px-2.5 py-2' : ''} whitespace-nowrap ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
                                                    } ${column.className || ''}`}
                                            >
                                                {column.render ? column.render(item) : (
                                                    typeof column.accessor === 'function'
                                                        ? column.accessor(item)
                                                        : (item[column.accessor as keyof T] as unknown as string)
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                    {isExpanded && renderExpandedRow && (
                                        <tr className="bg-black/20">
                                            <td colSpan={columns.length} className="p-0">
                                                {renderExpandedRow(item)}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}
